import { useState, useEffect, use } from "react";
import { NavLink as NavLinkRRD, Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../apiHandles/axiosInstance";
import { Row, Col, Card, CardHeader, CardBody } from "reactstrap";
import { FaHome, FaBars, FaTimes } from 'react-icons/fa';
import "../../assets/css/argon-dashboard-react.css"
import { sideBarLinks, GreekTranslate, stagesNames } from "../translation.js"
import { PropTypes } from "prop-types";
import { usePremium } from "context/PremiumProvider";
import { Diamond } from 'lucide-react';
import { useLanguage } from "context/LanguageProvider";


import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
  Button,
  CardColumns,
} from "reactstrap";
import logo2 from "../../assets/img/logo2.svg";
import { useDarkMode } from "context/DarkModeProvider";

const subIds = {
  "Directed": 6,
  "Undirected": 7,
  "Binary Tree": 3,
  "AVL Tree": 4,
  "Heap": 5,
  "Singly Linked List": 9,
  "Doubly Linked List": 10,
  "1D Array": 11,
  "2D Array": 12,
  "Array-based Stack": 15,
  "Linked List Stack": 16,
  "Circular Queue": 17,
  "Priority Queue": 18,
  "Chaining": 19,
  "Open Addressing": 20,
  "Hash Set": 21,
  "Tree Set": 22,
  "Dynamic Array": 24,
  "Basics": 35,
  "Advanced": 36
};



function useSidebarLayout(isCollapsed, isMobileMenuOpen) {
  const location = useLocation();
  const hasSidebar = !/Settings|homepage|Homepage/i.test(location.pathname);

  const { darkMode } = useDarkMode()

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const mainContent = document.querySelector('.main-content') ||
      document.querySelector('[class*="main"]') ||
      document.querySelector('main') ||
      document.querySelector('#root > div:not(.navbar-vertical)');
    const pageBackground = darkMode ? '#0F172A' : 'var(--lightModeBack)';

    // Base styles

    body.style.backgroundColor = pageBackground;
    html.style.backgroundColor = pageBackground;
    body.style.margin = '0';
    body.style.padding = '0';

    const isMobile = window.innerWidth <= 768;
    const sidebarWidth = isCollapsed ? '70px' : '250px';

    if (mainContent) {
      mainContent.style.transition = 'all 0.3s cubic-bezier(0.4,0,0.2,1)';
      mainContent.style.backgroundColor = pageBackground;
      mainContent.style.minHeight = '100vh';
      mainContent.style.boxSizing = 'border-box';
    }

    if (hasSidebar) {
      if (isMobile) {
        // Mobile: Sidebar overlay, no margin adjustment
        body.style.marginLeft = '0';
        if (mainContent) {
          mainContent.style.marginLeft = '0';
          mainContent.style.width = '100%';
          mainContent.style.padding = '20px';
        }
        // Prevent scrolling when mobile menu is open
        if (isMobileMenuOpen) {
          body.style.overflow = 'hidden';
        } else {
          body.style.overflow = 'auto';
        }
      } else {
        // Desktop: Fixed sidebar with margin
        body.style.overflow = 'auto';
        body.style.marginLeft = '0'; // Remove body margin
        if (mainContent) {
          mainContent.style.marginLeft = sidebarWidth;
          mainContent.style.width = `calc(100% - ${sidebarWidth})`;
          mainContent.style.padding = '20px';
        }
      }
    } else {
      // No sidebar pages
      body.style.marginLeft = '0';
      body.style.overflow = 'auto';
      if (mainContent) {
        mainContent.style.marginLeft = '0';
        mainContent.style.width = '100%';
        mainContent.style.padding = '20px';
      }
    }

    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        // Trigger re-render on breakpoint change
        window.location.reload();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      body.style.overflow = 'auto'; // Cleanup overflow lock
    };
  }, [isCollapsed, isMobileMenuOpen, location.pathname, hasSidebar, darkMode]);
}





const colorSchemes = {
  HashTable: { primary: '#10b981', secondary: '#059669', accent: '#d1fae5' },
  Tree: { primary: '#3b82f6', secondary: '#2563eb', accent: '#dbeafe' },
  List: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#ede9fe' },
  Stack: { primary: '#f59e0b', secondary: '#d97706', accent: '#fef3c7' },
  Queue: { primary: '#ef4444', secondary: '#dc2626', accent: '#fecaca' },
  Graph: { primary: '#06b6d4', secondary: '#0891b2', accent: '#cffafe' },
  Array: { primary: '#ec4899', secondary: '#db2777', accent: '#fce7f3' },
  Set: { primary: '#84cc16', secondary: '#65a30d', accent: '#ecfccb' },
  Python: { primary: '#3776ab', secondary: '#ffd43b' }

};


const Sidebar = (props) => {
  const [finalStructure, setFinalStructure] = useState(localStorage.getItem("structureLocation") || "");
  const finalStage = localStorage.getItem("stageLocation") || "";
  const finalSubstructure = localStorage.getItem("substrcutureLocation") || "";

  const [hoveredButton, setHoveredButton] = useState(null);
  const [clickedButton, setClickedButton] = useState(null);
  const { darkMode } = useDarkMode();
  const { isPremium } = usePremium();




  const { selLanguage, toggleLanguage, isLoading } = useLanguage();


  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "false" ? false : true; // Default to true if not set
  });

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const hidePath = /MyProfile|Premium|Feedback|classDetails|Settings/i.test(location.pathname);
  const isMobile = window.innerWidth <= 768;

  // Save collapse state
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
  }, [isCollapsed]);



  useEffect(() => {
    const currentStructure = localStorage.getItem("structureLocation") || "";
    setFinalStructure(currentStructure);
  }, [location.pathname]);


  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Use the improved hook
  useSidebarLayout(isCollapsed, isMobileMenuOpen);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setClickedButton('logout');
    setTimeout(() => {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      delete axiosInstance.defaults.headers.common["Authorization"];
      setIsMobileMenuOpen(false);
      navigate("/login");
    }, 150);
  };

  const handleHomepage = () => {
    setIsMobileMenuOpen(false);
    navigate("/Homepage/homepage");
  };

  const handleSubstructureClick = () => {
    setIsMobileMenuOpen(false);
    navigate(`/structure/${finalStructure}/${subIds[finalSubstructure]}`);
  };

  const getActionButtonStyle = (buttonType, isHovered = false) => {
    return {
      color: isHovered ? "#ffffff" : "#b9c1ccff",
      fontWeight: "400",
      fontFamily: "Poppins, sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
      padding: (isCollapsed && !isMobile) ? '10px' : '12px 16px',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      width: (isCollapsed && !isMobile) ? '40px' : '100%',
      minHeight: (isCollapsed && !isMobile) ? '40px' : 'auto',
      border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.2)',
      background: isHovered ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
      cursor: 'pointer',
      transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      marginBottom: (isCollapsed && !isMobile) ? '10px' : '5px',
      marginRight: (isCollapsed && !isMobile) ? "25px" : null,
      boxShadow: isHovered ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
    };
  };

  function limitRoots(routes) {
    return routes.filter(
      (route) =>
        route.name !== "Register" &&
        route.name !== "Login" &&
        route.name !== "Homepage" &&
        route.name !== "Examples" &&
        route.name !== "MyProfile" &&
        route.name !== "Settings" &&
        route.name !== "GeneralStage" &&
        route.name !== "Password Reset" &&
        route.name !== "Premium" &&
        route.name !== "Feedback" &&
        route.name !== "classDetails"
    );
  }

  const createLinks = (routes) => {
    const limitedRoutes = limitRoots(routes);
    return limitedRoutes.map((prop, key) => {
      const isActive = location.pathname.indexOf(prop.layout + prop.path) > -1;

      return (
        <NavItem
          key={key}
          className="sidebar-nav-item"
          style={{
            display: 'flex',
            justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
            marginBottom: (isCollapsed && !isMobile) ? '10px' : '5px',
            marginRight: (isCollapsed && !isMobile) ? "25px" : null,
          }}
        >
          <NavLink
            to={prop.layout + prop.path}
            tag={NavLinkRRD}
            onClick={(e) => {
              if (!isPremium && prop.name === "Create") {
                e.preventDefault();
                return;
              }
              closeMobileMenu();
            }}
            style={{
              color: isActive
                ? (darkMode ? "#eeeeeeff" : "#333333ff")
                : (darkMode ? "var(--commonProfileGrey)" : "#636a73ff"),
              fontWeight: isActive ? "600" : "400",
              fontFamily: "Poppins, sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: (isCollapsed && !isMobile) ? "center" : "flex-start",
              padding: (isCollapsed && !isMobile) ? "10px" : "12px 16px",
              borderRadius: "8px",
              transition: "all 0.3s ease",
              textDecoration: "none",
              width: (isCollapsed && !isMobile) ? "40px" : "100%",
              minHeight: (isCollapsed && !isMobile) ? "40px" : "auto",
              cursor: !isPremium && prop.name === "Create" ? "not-allowed" : "pointer",
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.07)"}
            onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}

            title={
              !isPremium && prop.name === "Create"
                ? "Available only in Premium"
                : undefined
            }
          >

            <i
              className={prop.icon}
              style={{
                fontSize: (isCollapsed && !isMobile) ? "16px" : "14px",
                marginRight: (isCollapsed && !isMobile) ? "0" : "8px",
              }}
            />
            {(!isCollapsed || isMobile) && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                {selLanguage ? sideBarLinks[prop.name] : prop.name}
                {prop.name === "Create" && (
                  <i
                    className="ni ni-diamond mb-3"
                    style={{ fontSize: "14px", color: "#20bde0ff" }}
                  />
                )}
              </span>
            )}
          </NavLink>
        </NavItem >
      );
    });
  };

  const { routes, logo } = props;

  // Mobile Menu Toggle Button (fixed position)
  if (isMobile) {
    return (
      <>
        {/* Mobile Toggle Button */}
        <Button
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 1002,
            width: '50px',
            height: '50px',
            borderRadius: '12px',
            border: 'none',
            background: 'rgba(30, 41, 59, 0.95)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            //boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {isMobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
        </Button>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              // zIndex: 1000,
              animation: 'fadeIn 0.3s ease',
            }}
            onClick={closeMobileMenu}
          />
        )}

        {/* Mobile Sidebar */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: isMobileMenuOpen ? 0 : '-300px',
            width: '280px',
            height: '100vh',
            background: '#050e22ff',
            zIndex: 1001,
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            //boxShadow: '2px 0 10px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Mobile Logo */}
          <div style={{ marginBottom: '30px', marginTop: '40px' }}>
            <Button
              onClick={handleHomepage}
              style={{
                border: "none",
                padding: "5px 0px",
                fontSize: "28px",
                fontFamily: "Poppins, sans-serif",
                background: "#5e72e4",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                cursor: "pointer",
                borderRadius: "8px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "10px",
              }}
            >
              <img src={logo2} alt="Logo" style={{ height: "45px" }} />
              <span style={{ fontSize: "34px", fontWeight: 700 }}>
                DaSA
              </span>
            </Button>
          </div>

          {/* Mobile Navigation */}
          {!hidePath && (
            <div style={{ flex: 1 }}>
              {/* Breadcrumbs */}
              <div style={{
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: "Poppins, sans-serif",
                marginBottom: '20px'
              }}>
                {finalStructure && (
                  <span onClick={handleHomepage} style={{
                    color: "#7a8effff",
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}>
                    {selLanguage ? sideBarLinks[finalStructure] : finalStructure}
                  </span>
                )}
                {finalSubstructure && (
                  <>
                    {finalStructure && ' / '}
                    <span onClick={handleSubstructureClick} style={{
                      color: "#7a8effff",
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}>
                      {selLanguage ? GreekTranslate[finalSubstructure] : finalSubstructure}
                    </span>
                  </>
                )}
                {finalStage && (
                  <>
                    {(finalStructure || finalSubstructure) && ' / '}
                    <span style={{ color: "#7a8effff" }}>
                      {selLanguage ? stagesNames[finalStage.toLocaleLowerCase()] : finalStage}
                    </span>
                  </>
                )}
              </div>

              <Nav navbar style={{ flexDirection: 'column' }}>
                {createLinks(routes)}
              </Nav>
            </div>
          )}

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            marginTop: 'auto',
            paddingTop: '20px'
          }}>


            {hidePath && (<NavItem className="sidebar-nav-item" style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginBottom: '5px',
            }}>
              <button
                onClick={handleLogout}
                onMouseEnter={(e) => {
                  setHoveredButton('logout');
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.color = '#ffffff';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  setHoveredButton(null);
                  e.target.style.transform = 'scale(1)';
                  e.target.style.color = '#b9c1ccff';
                  e.target.style.background = 'transparent';
                  e.target.style.boxShadow = 'none';
                }}
                style={getActionButtonStyle('logout', hoveredButton === 'logout')}
              >
                <i className="ni ni-user-run" style={{
                  fontSize: '14px',
                  marginRight: '8px'
                }} />
                <span>Logout</span>
              </button>
            </NavItem>)}
          </div>

        </div>

        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </>
    );
  }

  // Desktop Sidebar (original logic)
  return (
    <>
      <Navbar
        className="navbar-vertical fixed-left"
        expand="md"
        id="sidenav-main"
        style={{
          background: darkMode ? "#050e22ff" : "#deeffcf9",
          width: isCollapsed ? '70px' : '250px',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'visible',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          // zIndex: 1000,
        }}
      >
        <Container fluid style={{
          padding: isCollapsed ? '0 10px' : '0 20px',
          transition: 'padding 0.3s ease',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',

        }}>
          {/* Desktop Logo */}
          <div style={{
            display: 'flex',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            marginBottom: '20px',
            transition: 'all 0.3s ease',
            width: "110%",

          }}>
            {isCollapsed ? (
              <img
                src={logo2}
                alt="Logo"
                style={{
                  height: "35px",
                  cursor: "pointer",
                }}
                onClick={handleHomepage}
              />
            ) : (
              <Button
                onClick={handleHomepage}
                style={{
                  border: "none",
                  padding: "5px 0px",
                  fontSize: "32px",
                  fontFamily: "Poppins, sans-serif",
                  background: "#5e72e4",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "10px",
                  fontSize: "37px",
                  outline: "none",
                  boxShadow: "none",
                }}
              >
                <img src={logo2} alt="Logo" style={{ height: "45px" }} />
                <span>DaSA</span>
              </Button>
            )}
          </div>



          {/* Desktop Content */}
          {!hidePath && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Col style={{
                fontSize: "15px",
                fontWeight: "500",
                marginTop: isCollapsed ? "20px" : "5px",
                fontFamily: "Poppins, sans-serif",
                transition: 'all 0.3s ease',
                flex: 1,
              }}>
                {!isCollapsed && (
                  <div style={{ marginBottom: '20px' }}>
                    <Row>
                      {finalStructure && (
                        <Link to="/HomePage/homepage"
                          onClick={() => {
                            localStorage.setItem("cameFromLink", true);
                            handleHomepage(); // ✅ εδώ χρειάζεται ()
                          }}

                          style={{
                            color: "#7a8effff",
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: "90%"
                          }}>
                          {selLanguage ? sideBarLinks[finalStructure] : finalStructure}
                        </Link>
                      )}
                      {finalSubstructure && (
                        <>
                          {finalStructure && ' / '}
                          <span onClick={handleSubstructureClick} style={{
                            color: "#7a8effff",
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: "90%"
                          }}>
                            {selLanguage ? GreekTranslate[finalSubstructure] : finalSubstructure}
                          </span>
                        </>
                      )}
                      {finalStage && (
                        <>
                          {(finalStructure || finalSubstructure) && ' / '}
                          <Link to="/admin/index" style={{
                            color: "#7a8effff",
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: "90%"
                          }}>
                            {selLanguage ? stagesNames[finalStage.toLocaleLowerCase()] : finalStage}
                          </Link>
                        </>
                      )}
                    </Row>
                  </div>
                )}

                <Nav navbar style={{
                  flexDirection: 'column',
                  alignItems: isCollapsed ? 'center' : 'stretch'
                }}>
                  {createLinks(routes)}
                </Nav>

                {!isCollapsed && <hr className="my-3" />}
              </Col>
            </div>
          )}


          <div style={{
            position: "absolute",
            top: "75%",
            right: isCollapsed ? "0px" : "0px",
            transform: "translateY(-50%)",
            zIndex: 1051,
            transition: 'all 0.3s ease'
          }}>
            <Button
              onClick={toggleSidebar}

              style={{
                width: '24px',
                height: '36px',
                borderRadius: '8px',
                border: "none",
                outline: 'none',
                background: 'transparent',
                backdropFilter: 'none',
                color: darkMode ? '#dadadaff' : '#333333ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '25px',
                fontWeight: '500',
                boxShadow: 'none',
                marginBottom: "10px",
                padding: 0,
              }}
            >
              {/* Δύο chevron βελάκια (μόνο μύτη) */}
              <span style={{
                display: 'flex',
                gap: '1px',
                opacity: 0.8,
                transition: 'all 0.3s ease',
              }}>
                {isCollapsed ? (
                  <>
                    <span>›</span>
                  </>
                ) : (
                  <>
                    <span>‹</span>
                  </>
                )}
              </span>
            </Button>
          </div>
          <style jsx>{`
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`}</style>


          {hidePath && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: isCollapsed ? "center" : "stretch",
              justifyContent: "flex-end", // Αλλαγή από center σε flex-end
              gap: "0px",
              width: "100%",
              height: "auto", // Προσθήκη για καλύτερο control
              marginTop: "auto",
              marginBottom: "20px",
              paddingLeft: isCollapsed ? "0" : "12px",
              paddingBottom: "10px", // Προσθήκη padding για extra space
              transition: 'gap 0.3s ease'
            }}>

              <NavItem className="sidebar-nav-item" style={{
                display: 'flex',
                justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
                marginBottom: (isCollapsed && !isMobile) ? '10px' : '5px',
                marginLeft: (isCollapsed && !isMobile) ? "18px" : null,
              }}>
                <button

                  onClick={handleLogout}
                  onMouseEnter={(e) => {
                    setHoveredButton('logout');
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.color = '#ffffff';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    setHoveredButton(null);
                    e.target.style.transform = 'scale(1)';
                    e.target.style.color = '#b9c1ccff';
                    e.target.style.background = 'transparent';
                    e.target.style.boxShadow = 'none';
                  }}
                  style={getActionButtonStyle('logout', hoveredButton === 'logout')}
                  title={isCollapsed ? "Logout" : ""}
                >
                  <i className="ni ni-user-run" style={{
                    fontSize: (isCollapsed && !isMobile) ? '16px' : '14px',
                    marginRight: (isCollapsed && !isMobile) ? '0' : '8px',
                    color: darkMode ? '#ffffff' : '#161a21ff',
                  }} />
                  {!isCollapsed && <span
                    style={{ color: darkMode ? "#ffffff" : "#161a21ff" }}
                  >
                    {selLanguage ? "Αποσύνδεση" : "Logout"}</span>}
                </button>
              </NavItem>
            </div>
          )}
        </Container>
      </Navbar >
    </>
  );
};

Sidebar.defaultProps = {
  routes: [{}],
};

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
  }),
};

export default Sidebar;